import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Typography, Space, message, Spin } from 'antd';
import {
  ReloadOutlined,
  MessageOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { getCaptcha, checkCaptcha } from '@/services/captcha';
import { bus } from '@/utils/bus';
import { initial, set } from 'lodash';
// ------------------------------------------------------------------
// Props
// ------------------------------------------------------------------
export interface RotateCaptchaModalProps {
  visible: boolean;
  name: string; // getCaptcha 需要的 name 参数
  verifyUuid?: string; // 验证唯一标识
  verifyType?: string; // 验证类型，默认为 '102'
  rid?: string; // 请求 ID
  backgroundUrl?: string; // 任意尺寸
  centerUrl?: string; // 圆形碎片
  bgDisplayWidth?: number; // 背景渲染宽度，默认 336
  tolerance?: number; // 验证容差
  pieceExtraPx?: number; // 调整碎片直径
  onClose?: () => void;
  onVerify?: (angle: number, passed: boolean) => void;
  onCaptchaUpdate?: (data: {
    verifyUuid: string;
    verifyType?: string; // 可选，默认为 '102'
    rid: string;
    backgroundUrl: string;
    centerUrl: string;
  }) => void; // 验证码更新时的回调
}

/**
 * 通用旋转验证码（修复 “Rendered more hooks than during previous render”）。
 * 关键：**所有 hooks 均在顶层声明**，渲染逻辑用变量分支，而非提前 return。
 */
const RotateCaptchaModal: React.FC<RotateCaptchaModalProps> = ({
  visible,
  onClose,
  name,
  verifyUuid: initialVerifyUuid,
  verifyType: initialVerifyType = '102', // 默认值为 '102'
  rid: initialRid,
  backgroundUrl: initialBackgroundUrl,
  centerUrl: initialCenterUrl,
  bgDisplayWidth = 336,
  pieceExtraPx = 0,
  onVerify,
  onCaptchaUpdate,
}) => {
  // -------------------------
  // 验证码数据状态
  // -------------------------
  const [verifyUuid, setVerifyUuid] = useState<string | undefined>(
    initialVerifyUuid,
  );
  const [rid, setRid] = useState<string | undefined>(initialRid);
  const [verifyType, setVerifyType] = useState<string | undefined>(
    initialVerifyType,
  );
  const [backgroundUrl, setBackgroundUrl] = useState<string | undefined>(
    initialBackgroundUrl,
  );
  const [centerUrl, setCenterUrl] = useState<string | undefined>(
    initialCenterUrl,
  );
  const [refreshing, setRefreshing] = useState(false);

  // 当 props 变化时更新内部状态
  useEffect(() => {
    setVerifyUuid(initialVerifyUuid);
    setRid(initialRid);
    setBackgroundUrl(initialBackgroundUrl);
    setCenterUrl(initialCenterUrl);
    setVerifyType(initialVerifyType);
  }, [
    initialVerifyUuid,
    initialRid,
    initialBackgroundUrl,
    initialCenterUrl,
    initialVerifyType,
  ]);

  // -------------------------
  // 预加载图片尺寸
  // -------------------------
  const [bgNatural, setBgNatural] = useState<{ w: number; h: number } | null>(
    null,
  );
  const [pieceNaturalDia, setPieceNaturalDia] = useState<number | null>(null);

  const preload = useCallback((src: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (!backgroundUrl || !centerUrl) return;

    let mounted = true;
    Promise.all([preload(backgroundUrl), preload(centerUrl)])
      .then(([bgImg, pieceImg]) => {
        if (!mounted) return;
        setBgNatural({ w: bgImg.naturalWidth, h: bgImg.naturalHeight });
        setPieceNaturalDia(pieceImg.naturalWidth); // 假设正圆
      })
      .catch(() => message.error('加载验证码图片失败'));
    return () => {
      mounted = false;
    };
  }, [backgroundUrl, centerUrl, preload]);

  // -------------------------
  // 状态 & refs (始终声明)
  // -------------------------
  const handleSize = 40;
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [angle, setAngle] = useState(0);
  const [passed, setPassed] = useState(false);

  // 鼠标轨迹记录相关状态
  const [dragStartTime, setDragStartTime] = useState<number>(0);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [dragStartY, setDragStartY] = useState<number>(0);
  const [mouseTrajectory, setMouseTrajectory] = useState<
    [number, number, number][]
  >([]);

  // 事件处理 (拖拽 -> angle)
  const offsetToAngle = (offset: number, trackW: number) => {
    const max = trackW - handleSize;
    return (Math.min(Math.max(offset, 0), max) / max) * 360;
  };
  const updateFromX = (x: number) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    setAngle(offsetToAngle(x - rect.left - handleSize / 2, rect.width));
  };
  const verify = async (
    deg: number,
    mouseEnd: number,
    duration: number,
    trajectory: [number, number, number][],
  ) => {
    const verified = await checkCaptcha({
      verifyUuid: verifyUuid!,
      verifyType: verifyType!,
      mouseEnd: String(mouseEnd),
      time: String(duration),
      track: JSON.stringify(trajectory),
      rid: rid!,
      name,
    });
    if (!verified || !verified['data']['result']['success']) {
      message.error('验证失败，请重试');
      setPassed(false);
      refreshCaptcha();
      return;
    }
    setPassed(true);
    reset();
    setBgNatural(null);
    setPieceNaturalDia(null);
    bus.emit('polling:resume');
    message.success('验证通过');

    onVerify?.(deg, true);
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (!dragging) return;

      // 记录鼠标轨迹 - 相对于开始位置的偏移量，四舍五入取整
      const currentTime = Date.now();
      const deltaX = Math.round(e.clientX - dragStartX);
      const deltaY = Math.round(e.clientY - dragStartY);
      const timeFromStart = currentTime - dragStartTime;

      setMouseTrajectory((prev) => [...prev, [deltaX, deltaY, timeFromStart]]);

      updateFromX(e.clientX);
    };

    const up = (e: PointerEvent) => {
      if (!dragging) return;
      setDragging(false);
      updateFromX(e.clientX);

      // 计算最终数据，四舍五入取整
      const endTime = Date.now();
      const duration = endTime - dragStartTime;
      const mouseEnd = Math.round(e.clientX - dragStartX);

      verify(angle, mouseEnd, duration, mouseTrajectory);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging, angle, dragStartTime, dragStartX, dragStartY, mouseTrajectory]);

  const translateX = trackRef.current
    ? `${
        (angle / 360) *
        (trackRef.current.getBoundingClientRect().width - handleSize)
      }px`
    : '0px';

  const reset = () => {
    setAngle(0);
    setPassed(false);
    setMouseTrajectory([]);
    setDragStartTime(0);
    setDragStartX(0);
    setDragStartY(0);
  };

  const refreshCaptcha = async () => {
    if (!verifyUuid) return;

    try {
      setRefreshing(true);

      // 先重置状态
      reset();
      setBgNatural(null);
      setPieceNaturalDia(null);

      const { data: newCaptchaData } = await getCaptcha({
        verifyUuid,
        verifyType: verifyType || '102', // 默认值为 '102'
        name,
      });

      // 再更新验证码数据
      // setVerifyUuid(newCaptchaData.verifyUuid);
      setRid(newCaptchaData.rid);
      setBackgroundUrl(newCaptchaData.captchaInfo.backgroundUrl);
      setCenterUrl(newCaptchaData.captchaInfo.captchaUrl);

      // 通知父组件验证码已更新
      onCaptchaUpdate?.(newCaptchaData);

      message.success('验证码已刷新');
    } catch (error) {
      console.error('刷新验证码失败:', error);
      message.error('刷新验证码失败，请重试');
    } finally {
      setRefreshing(false);
    }
  };

  // -------------------------
  // 计算展示尺寸（当图片加载完才计算）
  // -------------------------
  const isLoaded = bgNatural && pieceNaturalDia !== null;
  const scale = isLoaded ? bgDisplayWidth / bgNatural!.w : 1;
  const bgDisplayHeight = isLoaded
    ? bgNatural!.h * scale
    : bgDisplayWidth * 0.6;
  const pieceDisplayDia = isLoaded
    ? pieceNaturalDia! * scale + pieceExtraPx
    : 0;
  const modalWidth = bgDisplayWidth + 48;

  // -------------------------
  // 渲染
  // -------------------------
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={modalWidth}
      bodyStyle={{ padding: 24, borderRadius: 16 }}
      closeIcon={null}
      zIndex={9999}
    >
      <Typography.Title
        level={5}
        style={{ textAlign: 'center', marginBottom: 16 }}
      >
        {passed ? '验证成功' : '请通过验证'}
      </Typography.Title>

      {/* 背景区 */}
      <div
        style={{
          width: bgDisplayWidth,
          height: bgDisplayHeight,
          margin: '0 auto 16px',
          position: 'relative',
          borderRadius: 8,
          overflow: 'hidden',
          background:
            isLoaded && backgroundUrl
              ? `url(${backgroundUrl}) center/100% 100% no-repeat`
              : '#f5f5f5',
        }}
      >
        {!isLoaded || refreshing ? (
          <Spin style={{ position: 'absolute', inset: 0, margin: 'auto' }} />
        ) : centerUrl ? (
          <img
            src={centerUrl}
            alt="captcha-piece"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: pieceDisplayDia,
              height: pieceDisplayDia,
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </div>

      {/* 滑块轨道 */}
      <div
        ref={trackRef}
        style={{
          width: bgDisplayWidth,
          height: 40,
          margin: '0 auto',
          position: 'relative',
          borderRadius: 24,
          background: passed ? '#f5f5f5' : '#f8f8f8',
          boxShadow: 'inset 0 0 0 1px #eaeaea',
          userSelect: 'none',
        }}
      >
        <Typography.Text
          type="secondary"
          style={{
            position: 'absolute',
            left: handleSize + 8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          请向右拖动滑块完成上方拼图
        </Typography.Text>
        <div
          onPointerDown={(e) => {
            const startTime = Date.now();
            const startX = e.clientX;
            const startY = e.clientY;

            setDragging(true);
            setDragStartTime(startTime);
            setDragStartX(startX);
            setDragStartY(startY);
            setMouseTrajectory([]);

            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 52,
            height: 40,
            borderRadius: '999px',
            background: passed ? '#d9d9d9' : '#fff',
            boxShadow: '0 0 4px rgba(0,0,0,.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            transform: `translateX(${translateX})`,
            transition: dragging ? 'none' : 'transform 0.2s',
          }}
        >
          <ArrowRightOutlined />
        </div>
      </div>

      {/* 底部操作 */}
      <Space
        size={24}
        style={{ marginTop: 24, justifyContent: 'center', width: '100%' }}
      >
        <Space
          direction="vertical"
          align="center"
          onClick={refreshCaptcha}
          style={{
            cursor: refreshing ? 'not-allowed' : 'pointer',
            opacity: refreshing ? 0.6 : 1,
          }}
        >
          <ReloadOutlined spin={refreshing} />
          <Typography.Text style={{ fontSize: 12 }}>刷新</Typography.Text>
        </Space>
        <Space
          direction="vertical"
          align="center"
          style={{ cursor: 'pointer' }}
        >
          <MessageOutlined />
          <Typography.Text style={{ fontSize: 12 }}>反馈</Typography.Text>
        </Space>
      </Space>
    </Modal>
  );
};

export default RotateCaptchaModal;
